import { useEffect, useState } from 'react';

import Spinner from '../Components/ui/Spinner';
import { formatToReadableDate } from '../Helpers/dateUtils';
import getScheduledCallbackService from '../Services/getScheduledCallbackService';
import getSchedulerAds from '../Services/getSchedulerAds';
import CallbackComponent from './Partials/Components/CallbackComponent';

import CallbackExpired from './Partials/Components/CallbackExpired';
import { getHourDifference } from './Partials/Components/CallbackHelpers';
import CallbackSummary from './Partials/Components/CallbackSummary';

const Callback = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isValidLink, setIsValidLink] = useState(false);
    const [schedulerData, setCallbackData] = useState(null);
    const [hasValidCallback, setHasValidCallback] = useState(false);
    const [pageAds, setPageAds] = useState([]);
    const [summaryData, setSummaryData] = useState(null);

    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Helper function to convert call center hours to local time
    const convertToLocalHours = (startHour, endHour) => {
        const hourDiff = getHourDifference();
        return {
            localStartHour: (startHour + hourDiff + 24) % 24,
            localEndHour: (endHour + hourDiff + 24) % 24,
        };
    };

    // Main validation function
    const validateCallback = async () => {
        setIsLoading(true); // Show loading spinner during validation
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const callbackHashKey = urlParams.get('id');
            const isEdit = urlParams.get('edit') === '1';

            if (!callbackHashKey) {
                setIsValidLink(false);
                return;
            }

            // Fetch scheduler ads
            const schedulerAds = await getSchedulerAds(callbackHashKey);
            setPageAds(schedulerAds?.data || []);

            // Fetch scheduled callback data
            const scheduledCallback = await getScheduledCallbackService(callbackHashKey);
            if (!scheduledCallback?.success) {
                setIsValidLink(false);
                return;
            }

            setIsValidLink(true);
            const responseData = scheduledCallback.data;

            // Handle valid callback with reminder
            if (!isEdit && responseData.has_reminder) {
                setHasValidCallback(true);
                setSummaryData({
                    comment: responseData.comment,
                    customerName: responseData.contact_name,
                    dateTime: formatToReadableDate(new Date(responseData.callback_at), browserTimezone),
                    phoneNumber: responseData.phone_number,
                });
            }

            // Parse callback time
            responseData.callback_time = responseData.callback_at || '';
            // Convert call center hours to local time
            const { localStartHour, localEndHour } = convertToLocalHours(
                responseData.cc_start_hour,
                responseData.cc_end_hour
            );
            responseData.cc_start_hour = localStartHour;
            responseData.cc_end_hour = localEndHour;

            // Update scheduler data
            setCallbackData(responseData);
        } catch (error) {
            console.error('Error validating callback:', error);
            setIsValidLink(false);
        } finally {
            setIsLoading(false); // Hide loading spinner after validation
        }
    };

    // Run validation on initial render
    useEffect(() => {
        validateCallback();
    }, []);

    return (
        <>
            {isLoading ? (
                <Spinner show={true} fullScreen={true} />
            ) : isValidLink ? (
                hasValidCallback ? (
                    <CallbackSummary data={summaryData} />
                ) : (
                    <CallbackComponent schedulerData={schedulerData} pageAds={pageAds} />
                )
            ) : (
                <CallbackExpired pageAds={pageAds} />
            )}
        </>
    );
};

export default Callback;
